import React from 'react';
import DocumentTitle from 'react-document-title';
import { useTranslation } from 'react-i18next';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { errorMessage, customMessage } from 'actions/internal';
import TopNavBar from 'containers/nav/TopNavBar';
import SubNavBar from 'containers/nav/SubNavBar';
import AdministrationSubNav from 'components/administration/AdministrationSubNav';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { appNamedRoutes } from 'routes';
import {
  useMutation,
} from '@tanstack/react-query'
import axios from 'axios'
import {
  useQueryClient,
  useQuery,
} from '@tanstack/react-query'
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { useForm, FormProvider } from 'react-hook-form';
import CohortSelect from './CohortSelect';
import PartnerCustomTagField from '../PartnerCustomTagField';

const AdministrationStaffNewPage = ({
  auth,
  onErrorMessage,
  onCustomMessage,
  history,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formMethods = useForm({
    defaultValues: {
      partner_id: auth.partnerId,
    },
  })

  const accesses = useQuery({
    queryKey: ['administration', 'organization', 'my-accesses'],
    queryFn: ({ signal }) => axios.get(
      Routes.api_dashboard_administration_organizations_my_accesses_path(),
      { headers: authHeaders(), signal }
    ).then(response => response.data),
  })

  const createAdmin = useMutation({
    mutationFn: (admin) => {
      return axios.post(Routes.api_dashboard_administration_admin_users_path(),
        { admin_user: admin }, { headers: authHeaders() })
    },
    onSuccess: () => {
      onCustomMessage(t('administration.staff.adminCreated'), 'success');
      queryClient.invalidateQueries({ queryKey: ['administration', 'admin-users'] }).then(() => {
        history.push(`${appNamedRoutes.administrationStaff}`);
      })
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage(t('errors.generic'));
        return
      }

      if (error.response.status === 422) {
        onErrorMessage(t('errors.formValidation'));

        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          formMethods.setError(key, {
            type: 'manual',
            message: errors[key].join(', '),
          });
        });

        return
      }
    },
  })

  const onSubmit = async (data) => {
    // rename the key partner_custom_tag_association to partner_custom_tag_association_attributes
    data.partner_custom_tag_association_attributes = data.partner_custom_tag_association
    delete data.partner_custom_tag_association

    createAdmin.mutate(data);
  };

  return (<DocumentTitle title={t('administration.pageTitle')}>
    <div>
      <TopNavBar />
      <SubNavBar />
      <AdministrationSubNav />
      <FormProvider {...formMethods}>
        <form onSubmit={(event) => {
          formMethods.clearErrors();
          formMethods.handleSubmit(onSubmit)(event);
        }}>
          <div className="row content">
            <div className="column">
              <div className='u-margin-top--base u-margin-bottom--large'>
                <span className='o-sp-button o-sp-button--back' onClick={() => {
                  const queryParams = history.location.search;
                  history.push(`${appNamedRoutes.administrationStaff}${queryParams}`)
                }}>
                  <i className='sp-icon-arrow-left'></i>&nbsp;{t('administration.staff.back')}
                </span>
              </div>
              <div className="row">
                <div className="column small-12">
                  <div className='o-card__wrap o-admin-card'>
                    <section className='column o-card cs-bg--light'>
                      <div className='u-flex u-flex-big-gap'>
                        <div className='u-flex-1'>
                          <h3><b>{t('administration.staff.newAdminTitle')}</b></h3>
                        </div>
                      </div>
                      <div className='u-margin-top--base'>
                        <input type='hidden' {...formMethods.register('partner_id')} />
                        <p>
                          <span className='u-txt--label'>{t('administration.staff.name')}</span><br />
                          <input type='text' {...formMethods.register('name')} aria-invalid={formMethods.formState.errors.name ? true : false} />
                          {formMethods.formState.errors.name && (<p className='u-txt--error'>{formMethods.formState.errors.name.message}</p>)}
                        </p>
                        <p>
                          <span className='u-txt--label'>{t('administration.staff.email')}</span><br />
                          <input type='email' {...formMethods.register('email')} aria-invalid={formMethods.formState.errors.email ? true : false} />
                          {formMethods.formState.errors.email && (<p className='u-txt--error'>{formMethods.formState.errors.email.message}</p>)}
                        </p>
                        <p>
                          <PartnerCustomTagField kind='admin_user_type' label={t('administration.staff.adminUserType')} />
                        </p>
                        <fieldset>
                          <legend>{t('administration.staff.organizationAccessTitle')}</legend>
                          <p>
                            <CohortSelect accesses={accesses} />
                          </p>
                        </fieldset>
                        { auth.isPartnerUsingSaml && (
                          <p>
                            <label>
                              <input type='checkbox' {...formMethods.register('using_saml')} />
                              <span className='u-txt--label u-margin-left--small'>{t('administration.staff.ssoLabel')}</span>
                            </label>
                          </p>
                        )}
                      </div>
                      <div className='rown'>
                        <div className='column small-12'>
                          <input type='submit' className='o-sp-button o-sp-button--update o-sp-button--primary' value={t('actions.save')} disabled={createAdmin.isPending} />
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  </DocumentTitle>
  );
}

AdministrationStaffNewPage.propTypes = {
  auth: PropTypes.object.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  onCustomMessage: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
}

const mapDispatchToProps = (dispatch) => {
  return {
    onHideLoading: () => {
      dispatch(hideLoading())
    },
    onShowLoading: () => {
      dispatch(showLoading())
    },
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, messageType) => {
      dispatch(customMessage(message, messageType))
    },
  }
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AdministrationStaffNewPage);
